import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { billingKeys, BILLING_STALE_TIME_MS } from '../../services/queryClient';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  MoreHorizontal,
  Plus,
  Upload,
} from 'lucide-react';
import { useStore } from '../../store';
import {
  ExportMenuButton,
  UserFacingExportError,
  type ExportFormat,
} from '../../components/common/ExportMenuButton';
import { PermissionSlug } from '../../constants/featureFlags';
import {
  completeRemittanceRemittancesRemittanceIdCompletePost as completeRemittance,
  deleteRemittanceRemittancesRemittanceIdDelete as deleteRemittance,
  downloadRemittanceRemittancesRemittanceIdDownloadGet as downloadRemittance,
  exportRemittancesRemittancesExportGet as exportRemittances,
  getRemittanceDetailRemittancesRemittanceIdGet as getRemittanceDetail,
  getRemittanceStatusCountsRemittancesStatusCountsGet as getRemittanceStatusCounts,
  listRemittancePayorsRemittancesPayorsGet as listRemittancePayors,
  listRemittancesRemittancesGet as listRemittances,
  reopenRemittanceRemittancesRemittanceIdReopenPost as reopenRemittance,
  uploadRemittanceRemittancesUploadPost as uploadRemittance,
} from '../../services/api/sdk.gen';
import type {
  RemittanceAdjustmentDetail,
  RemittanceClaimDisposition,
  RemittanceDetailResponse,
  RemittancePaymentDetail,
  RemittancePayorOption,
  RemittanceStatus,
  RemittanceStatusCounts,
  RemittanceSummary,
} from '../../services/api/types.gen';
import { useRemittanceTableQuery } from '../../hooks/useRemittanceTableQuery';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessage,
  getUserFacingApiMessageAsync,
} from '../../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import { AttachEvidenceAction } from './AttachEvidenceAction';
import { ServerDataTable } from './BillingDataTable';
import { buildRemittanceExportParams } from './billingExportParams';
import { RemittanceTableToolbar } from './RemittanceTableToolbar';
import { TabCountPill } from './TabCountPill';
import {
  REMARK_CODE_DESCRIPTIONS,
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
} from './remittanceLabels';
import RemittanceWorksheetEditor from './RemittanceWorksheetEditor';

/** The remittance pipeline, left to right — mirrors the claims status tabs. */
const STATUS_TABS: { id: RemittanceStatus; label: string }[] = [
  { id: 'saved', label: STATUS_LABELS.saved },
  { id: 'completed', label: STATUS_LABELS.completed },
];

const formatCurrency = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

const formatDate = (value: string | null | undefined): string =>
  value ? formatDateToMMDDYYYY(value) : '—';

function StatusBadge({ status }: { status: RemittanceStatus }) {
  return (
    <Badge variant='outline' className={`text-xs ${STATUS_VARIANTS[status]}`}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

const DISPOSITION_LABELS: Record<RemittanceClaimDisposition, string> = {
  paid: 'Paid',
  denied: 'Denied',
  reversed: 'Reversed',
  other: 'Other',
};

const DISPOSITION_VARIANTS: Record<RemittanceClaimDisposition, string> = {
  paid: 'text-green-700 border-green-300 bg-green-50',
  denied: 'text-red-700 border-red-300 bg-red-50',
  reversed: 'text-orange-700 border-orange-300 bg-orange-50',
  other: 'text-gray-700 border-gray-300 bg-gray-50',
};

function DispositionBadge({
  disposition,
}: {
  disposition: RemittanceClaimDisposition;
}) {
  return (
    <Badge
      variant='outline'
      className={`text-xs ${DISPOSITION_VARIANTS[disposition]}`}
    >
      {DISPOSITION_LABELS[disposition]}
    </Badge>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className='text-xs text-muted-foreground'>{label}</div>
      <div className='text-sm font-medium'>{value ?? '—'}</div>
    </div>
  );
}

function AdjustmentsTable({
  title,
  adjustments,
}: {
  title: string;
  adjustments: RemittanceAdjustmentDetail[];
}) {
  if (adjustments.length === 0) return null;
  return (
    <div>
      <h4 className='text-xs font-semibold text-muted-foreground mb-1'>
        {title}
      </h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Group</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className='text-right'>Amount</TableHead>
            <TableHead className='text-right'>Qty</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {adjustments.map((adj, i) => (
            <TableRow key={i}>
              <TableCell className='text-sm'>
                {adj.group_code
                  ? `${adj.group_code}${adj.group_code_description ? ` — ${adj.group_code_description}` : ''}`
                  : '—'}
              </TableCell>
              <TableCell className='text-sm'>
                {adj.reason_code}
                {adj.reason_description ? ` — ${adj.reason_description}` : ''}
              </TableCell>
              <TableCell className='text-sm text-right'>
                {formatCurrency(adj.adjustment_amount)}
              </TableCell>
              <TableCell className='text-sm text-right'>
                {adj.adjustment_quantity ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ClaimSection({
  payment,
  onAttached,
}: {
  payment: RemittancePaymentDetail;
  onAttached: () => void | Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const title = payment.claim_number
    ? `Claim ${payment.claim_number}`
    : payment.patient_control_number
      ? `PCN ${payment.patient_control_number}`
      : 'Claim';
  const patientName =
    [payment.patient_first_name, payment.patient_last_name]
      .filter(Boolean)
      .join(' ') || null;
  const serviceLines = payment.service_lines ?? [];
  const claimAdjustments = payment.claim_adjustments ?? [];

  return (
    <div className='border rounded-md'>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className='w-full px-3 py-2 flex items-center justify-between hover:bg-accent/40 transition-colors'>
          <div className='flex items-center gap-2'>
            {isOpen ? (
              <ChevronUp className='h-4 w-4 text-muted-foreground shrink-0' />
            ) : (
              <ChevronDown className='h-4 w-4 text-muted-foreground shrink-0' />
            )}
            <span className='text-sm font-medium'>{title}</span>
            <DispositionBadge disposition={payment.disposition} />
            {payment.claim_id == null && (
              <Badge
                variant='outline'
                className='text-xs text-amber-700 border-amber-300 bg-amber-50'
              >
                Not matched
              </Badge>
            )}
          </div>
          <span className='text-sm font-medium'>
            {formatCurrency(payment.claim_payment_amount)}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className='px-3 py-3 border-t space-y-3'>
          <div className='grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2'>
            <DetailField label='Patient' value={patientName} />
            <DetailField
              label='Patient Control #'
              value={payment.patient_control_number}
            />
            <DetailField
              label='Payer Claim Control #'
              value={payment.payer_claim_control_number}
            />
            <DetailField
              label='Claim Status Code'
              value={payment.claim_status_code}
            />
            <DetailField label='Type of Bill' value={payment.type_of_bill} />
            <DetailField
              label='Total Charge'
              value={formatCurrency(payment.total_claim_charge_amount)}
            />
            <DetailField
              label='Patient Responsibility'
              value={formatCurrency(payment.patient_responsibility_amount)}
            />
            <DetailField
              label='Coverage Amount'
              value={formatCurrency(payment.coverage_amount)}
            />
          </div>

          {payment.remark_codes && payment.remark_codes.length > 0 && (
            <div>
              <h4 className='text-xs font-semibold text-muted-foreground mb-1'>
                Remark Codes
              </h4>
              <div className='flex flex-col gap-1'>
                {payment.remark_codes.map(code => (
                  <div key={code} className='flex items-start gap-2'>
                    <Badge variant='outline' className='text-xs shrink-0'>
                      {code}
                    </Badge>
                    {REMARK_CODE_DESCRIPTIONS[code] && (
                      <span className='text-sm text-muted-foreground'>
                        {REMARK_CODE_DESCRIPTIONS[code]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {serviceLines.length > 0 && (
            <div>
              <h4 className='text-xs font-semibold text-muted-foreground mb-1'>
                Service Lines
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Rev Code</TableHead>
                    <TableHead>Proc Code</TableHead>
                    <TableHead className='text-right'>Charge</TableHead>
                    <TableHead className='text-right'>Paid</TableHead>
                    <TableHead className='text-right'>Units</TableHead>
                    <TableHead>Adjustments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serviceLines.map(line => {
                    const lineAdjustments = line.adjustments ?? [];
                    return (
                      <TableRow key={line.id}>
                        <TableCell className='text-sm'>
                          {formatDate(line.service_date)}
                        </TableCell>
                        <TableCell className='text-sm'>
                          {line.revenue_code || '—'}
                        </TableCell>
                        <TableCell className='text-sm'>
                          {line.procedure_code || '—'}
                        </TableCell>
                        <TableCell className='text-sm text-right'>
                          {formatCurrency(line.charge_amount)}
                        </TableCell>
                        <TableCell className='text-sm text-right'>
                          {formatCurrency(line.payment_amount)}
                        </TableCell>
                        <TableCell className='text-sm text-right'>
                          {line.units ?? '—'}
                        </TableCell>
                        <TableCell className='text-sm'>
                          {lineAdjustments.length > 0
                            ? lineAdjustments
                                .map(
                                  a => `${a.group_code ?? ''}${a.reason_code}`
                                )
                                .join(', ')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Manual reconciliation (EMR-1409): only an unmatched payment
              (claim_id == null) shows the attach control, and only for a
              claims:reconcile holder — the component gates itself. */}
          <AttachEvidenceAction payment={payment} onAttached={onAttached} />

          <AdjustmentsTable
            title='Claim Adjustments'
            adjustments={claimAdjustments}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function RemittanceDetailDialog({
  remittanceId,
  onClose,
}: {
  remittanceId: string | null;
  onClose: () => void;
}) {
  const showError = useStore(state => state.showError);
  const queryClient = useQueryClient();
  const [isDownloading, setIsDownloading] = useState(false);

  // Replaces a `let cancelled` closure flag: a superseded detail request now
  // resolves into its own cache entry rather than into the open dialog.
  const detailQuery = useQuery({
    queryKey: billingKeys.remittanceDetail(remittanceId ?? ''),
    queryFn: async () => {
      if (!remittanceId) {
        throw new Error('Failed to load remittance detail');
      }
      const resp = await getRemittanceDetail({
        path: { remittance_id: remittanceId },
      });
      if (resp.error || !resp.data) {
        // The user-facing message rides on the Error so the dialog can render
        // it directly, keeping the existing copy for this endpoint's 4xx cases.
        throw new Error(
          getUserFacingApiMessage(resp, 'Failed to load remittance detail')
        );
      }
      return resp.data;
    },
    enabled: remittanceId !== null,
  });

  const detail: RemittanceDetailResponse | null = detailQuery.data ?? null;
  const isLoading = detailQuery.isFetching;
  const error = detailQuery.error ? detailQuery.error.message : null;

  const handleDownload = async () => {
    if (!remittanceId) return;
    setIsDownloading(true);
    try {
      const resp = await downloadRemittance({
        path: { remittance_id: remittanceId },
        responseType: 'blob',
      });
      if (resp.error) {
        showError(
          await getUserFacingApiMessageAsync(
            resp,
            'Failed to download remittance file'
          )
        );
        return;
      }
      downloadResponseAsFile(resp, detail?.file_name || 'remittance.835');
    } catch (err) {
      console.error('Failed to download remittance file', err);
      showError('Failed to download remittance file');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={remittanceId !== null}
      onOpenChange={v => {
        if (!v) onClose();
      }}
    >
      {/* Height is capped, not fixed: DialogContent is a CSS grid, so a fixed
          h-[85vh] stretches its auto rows and opens a large gap under the title
          on tall screens when the claim sections are collapsed. */}
      <DialogContent className='max-w-5xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-3'>
            <span>
              Remittance
              {detail?.trace_number ? ` — ${detail.trace_number}` : ''}
            </span>
            {detail?.has_file && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => void handleDownload()}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2 className='h-3 w-3 animate-spin mr-1' />
                ) : (
                  <Download className='h-3 w-3 mr-1' />
                )}
                Download 835
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className='space-y-2 py-8'>
            <Skeleton className='h-6 w-full' />
            <Skeleton className='h-6 w-full' />
            <Skeleton className='h-6 w-2/3' />
          </div>
        )}

        {!isLoading && error && (
          <div className='flex items-center justify-center gap-2 py-8 text-sm text-red-600'>
            <AlertCircle className='h-4 w-4' />
            {error}
          </div>
        )}

        {!isLoading && !error && detail && (
          <div className='space-y-4'>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 border-b pb-4'>
              <DetailField
                label='Deposit Date'
                value={formatDate(detail.deposit_date)}
              />
              <DetailField
                label='Check Date'
                value={formatDate(detail.check_date)}
              />
              <DetailField label='Trace Number' value={detail.trace_number} />
              <DetailField label='Payer' value={detail.payer_display} />
              <DetailField label='Payee' value={detail.payee_name} />
              <DetailField
                label='Amount'
                value={formatCurrency(detail.amount)}
              />
              <DetailField
                label='Status'
                value={<StatusBadge status={detail.status} />}
              />
              <DetailField
                label='Source'
                value={SOURCE_LABELS[detail.source]}
              />
              <DetailField label='File Name' value={detail.file_name} />
              <DetailField
                label='Uploaded By'
                value={detail.uploaded_by_name}
              />
              <DetailField label='Comments' value={detail.comments} />
            </div>

            {(detail.payments ?? []).length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No claims in this remittance.
              </p>
            ) : (
              <div className='space-y-2'>
                {(detail.payments ?? []).map(payment => (
                  <ClaimSection
                    key={payment.id}
                    payment={payment}
                    onAttached={() =>
                      // A successful attach re-matches the payment, so the
                      // dialog must show the new match: invalidate this
                      // remittance's detail query, which refetches in place.
                      queryClient.invalidateQueries({
                        queryKey: billingKeys.remittanceDetail(
                          remittanceId ?? ''
                        ),
                      })
                    }
                  />
                ))}
              </div>
            )}

            <AdjustmentsTable
              title='Provider-Level Adjustments (PLB)'
              adjustments={detail.provider_adjustments ?? []}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UploadButton({ onUploaded }: { onUploaded: () => void }) {
  const showSuccess = useStore(state => state.showSuccess);
  const showError = useStore(state => state.showError);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const resp = await uploadRemittance({
        body: { file },
      });
      if (resp.error || !resp.data) {
        showError(getUserFacingApiMessage(resp, 'Failed to upload 835 file'));
        return;
      }
      const count = resp.data.items.length;
      showSuccess(
        `Uploaded ${count} remittance${count === 1 ? '' : 's'} successfully`
      );
      onUploaded();
    } catch (err) {
      console.error('Failed to upload 835 file', err);
      showError('Failed to upload 835 file');
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        accept='.txt,.835,.edi,.era,text/plain'
        onChange={e => void handleChange(e)}
        className='hidden'
      />
      {/* 'default' (h-8) is the closest built-in size to the h-9 filter controls
          on this row; 'sm' (h-6) read as noticeably short beside them. */}
      <Button
        size='default'
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
      >
        {isUploading ? (
          <Loader2 className='h-4 w-4 animate-spin mr-1' />
        ) : (
          <Upload className='h-4 w-4 mr-1' />
        )}
        {isUploading ? 'Uploading…' : 'Upload 835'}
      </Button>
    </>
  );
}

type WorksheetEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; remittanceId: string };

type LifecycleAction = 'complete' | 'reopen' | 'delete';

const LIFECYCLE_COPY: Record<
  LifecycleAction,
  { title: string; message: string; confirmLabel: string; error: string }
> = {
  complete: {
    title: 'Complete remittance?',
    message:
      'This posts the remittance payment results to matching claims. You can reopen it later to edit the worksheet.',
    confirmLabel: 'Complete remittance',
    error: 'Failed to complete remittance',
  },
  reopen: {
    title: 'Reopen remittance?',
    message:
      'This makes the worksheet editable again and removes payment amounts that still match this remittance. Claim statuses are not changed.',
    confirmLabel: 'Reopen remittance',
    error: 'Failed to reopen remittance',
  },
  delete: {
    title: 'Delete remittance?',
    message:
      'This hides the saved remittance from the worksheet list. This action cannot be undone.',
    confirmLabel: 'Delete remittance',
    error: 'Failed to delete remittance',
  },
};

export interface RemittanceManagerHandle {
  /** Re-fetch the remittance list at the CURRENT filters/sort/page, plus the payor
   *  options. Resolves when both requests settle. Does not reset filters or paging. */
  refresh: () => Promise<void>;
}

const RemittanceManager = forwardRef<RemittanceManagerHandle>((_, ref) => {
  const query = useRemittanceTableQuery();
  const showError = useStore(state => state.showError);
  // Page-wide hide-test-patients flag shared with the claims/NOA surfaces; the
  // list request carries it through query.toParams(), the tab counts send it
  // directly.
  const hideTestPatients = useStore(state => state.hideTestPatients);
  const canExportRemittances = useStore(state =>
    state.hasPermission(PermissionSlug.BILLING_EXPORT_REMITTANCES)
  );

  const queryClient = useQueryClient();
  const [selectedRemittanceId, setSelectedRemittanceId] = useState<
    string | null
  >(null);
  const [worksheetEditor, setWorksheetEditor] =
    useState<WorksheetEditorState | null>(null);
  const [pendingLifecycle, setPendingLifecycle] = useState<{
    action: LifecycleAction;
    remittance: RemittanceSummary;
  } | null>(null);
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const popupTriggerRef = useRef<HTMLElement | null>(null);
  const newRemittanceTriggerRef = useRef<HTMLButtonElement | null>(null);
  const menuPopupSelectionRef = useRef(false);
  const lifecycleTransitionTriggerRef = useRef<HTMLElement | null>(null);
  const lifecycleTransitionItemsRef = useRef<RemittanceSummary[] | null>(null);
  const lifecycleTransitionListAppliedRef = useRef(false);

  useEffect(() => {
    if (selectedRemittanceId || worksheetEditor || pendingLifecycle) return;

    const trigger = popupTriggerRef.current;
    popupTriggerRef.current = null;
    if (!trigger) return;

    if (trigger.isConnected) {
      trigger.focus();
    } else {
      newRemittanceTriggerRef.current?.focus();
    }
  }, [pendingLifecycle, selectedRemittanceId, worksheetEditor]);

  const params = query.toParams();

  const payorsQuery = useQuery({
    queryKey: billingKeys.remittancePayors(),
    queryFn: async () => {
      const resp = await listRemittancePayors({});
      if (resp.error || !resp.data) {
        throw new Error('Failed to load remittance payors');
      }
      return resp.data.items;
    },
    staleTime: BILLING_STALE_TIME_MS,
  });
  const payors = useMemo<RemittancePayorOption[]>(
    () => payorsQuery.data ?? [],
    [payorsQuery.data]
  );

  // Tab counts are agency-wide totals per status, NOT narrowed by the toolbar
  // filters (claims parity), so this key deliberately excludes `params` and is
  // not refetched when a filter changes. Hide-test-patients is the exception:
  // it is a population default for the whole surface, not a view filter, so it
  // is part of both the key and the request (claims meta parity).
  const statusCountsQuery = useQuery({
    queryKey: billingKeys.remittanceStatusCounts(hideTestPatients),
    queryFn: async () => {
      const resp = await getRemittanceStatusCounts({
        query: { hide_test_patients: hideTestPatients },
      });
      if (resp.error || !resp.data) {
        throw new Error('Failed to load remittance status counts');
      }
      return resp.data;
    },
    staleTime: BILLING_STALE_TIME_MS,
    // No `meta.errorToast`: non-fatal, so the pills fall back to '-' and the
    // shared handler in queryClient.ts logs without interrupting the user.
  });
  const statusCounts: RemittanceStatusCounts | null =
    statusCountsQuery.data ?? null;

  // The latest-wins request-id guard this replaced is now structural: each
  // distinct params object is its own cache entry, so a superseded response
  // resolves into an entry nobody is observing.
  const remittancesQuery = useQuery({
    queryKey: billingKeys.remittances(params),
    queryFn: async () => {
      const resp = await listRemittances({ query: params });
      if (resp.error || !resp.data) {
        console.error('Failed to load remittances', resp.error);
        throw new Error('Failed to load remittances');
      }
      return resp.data;
    },
    // Keeps the current page on screen while the next one loads, instead of
    // blanking the table on every page/sort change.
    placeholderData: keepPreviousData,
    staleTime: BILLING_STALE_TIME_MS,
    // Toasted once per failed fetch by the shared handler in queryClient.ts,
    // rather than from an effect that would re-fire on every remount.
    meta: {
      errorToast: { title: 'Unable to load remittances. Please try again.' },
    },
  });

  // A failed load blanks the table rather than leaving the previous page's rows
  // under a filter that did not return them.
  const isRemittancesError = remittancesQuery.isError;
  const items = useMemo<RemittanceSummary[]>(
    () => (isRemittancesError ? [] : (remittancesQuery.data?.items ?? [])),
    [isRemittancesError, remittancesQuery.data]
  );
  const total = isRemittancesError ? 0 : (remittancesQuery.data?.total ?? 0);
  const totalPages = isRemittancesError
    ? 0
    : (remittancesQuery.data?.total_pages ?? 0);
  // isPending ("nothing to show yet"), NOT isFetching ("a request is in
  // flight"). ServerDataTable swaps the rows out for a single "Loading..." row
  // whenever this is true, so isFetching would discard exactly the rows
  // keepPreviousData just retained and keep the blank-table flash on every
  // page/sort change. First load still shows the placeholder, because there is
  // no previous page to hold.
  const isLoading = remittancesQuery.isPending;

  // A refetch with rows already on screen (filter, sort, page, search, or the
  // page-level Refresh). Drives the subtle "Updating..." indicator.
  const isRefreshing =
    remittancesQuery.isFetching && !remittancesQuery.isPending;

  useEffect(() => {
    const listResultApplied = lifecycleTransitionItemsRef.current !== items;
    lifecycleTransitionItemsRef.current = items;

    if (listResultApplied && lifecycleTransitionTriggerRef.current) {
      lifecycleTransitionListAppliedRef.current = true;
    }
    if (
      !lifecycleTransitionListAppliedRef.current ||
      selectedRemittanceId ||
      worksheetEditor ||
      pendingLifecycle
    ) {
      return;
    }

    const trigger = lifecycleTransitionTriggerRef.current;
    lifecycleTransitionTriggerRef.current = null;
    lifecycleTransitionListAppliedRef.current = false;
    if (!trigger?.isConnected) {
      newRemittanceTriggerRef.current?.focus();
    }
  }, [items, pendingLifecycle, selectedRemittanceId, worksheetEditor]);

  /**
   * Reload every remittance query. Invalidation rather than refetch, so the list
   * reloads at whatever filters/sort/page are current when it fires — the
   * partial keys match all params variants.
   */
  const refreshAll = useCallback(async () => {
    await Promise.all([
      // throwOnError so a failed list reload rejects this promise. Invalidation
      // resolves successfully even when the refetch errors by default, which
      // would let the caller report "Remittances refreshed" next to the
      // load-failure toast.
      queryClient.invalidateQueries(
        { queryKey: billingKeys.remittances() },
        { throwOnError: true }
      ),
      // Payors and counts stay non-fatal: they degrade to an unfiltered
      // dropdown and '-' pills, which is not worth failing a refresh over.
      queryClient.invalidateQueries({
        queryKey: billingKeys.remittancePayors(),
      }),
      queryClient.invalidateQueries({
        queryKey: billingKeys.remittanceStatusCounts(),
      }),
    ]);
  }, [queryClient]);

  useImperativeHandle(
    ref,
    () => ({
      // The page-level Refresh button drives both views (EMR-257 req 3). Payors are
      // refetched too: a newly ingested 835 can introduce a payor the Insurance
      // filter has never seen. Filters/sort/page are deliberately preserved.
      refresh: refreshAll,
    }),
    [refreshAll]
  );

  const handleDownloadRow = useCallback(
    async (item: RemittanceSummary) => {
      setDownloadingId(item.id);
      try {
        const resp = await downloadRemittance({
          path: { remittance_id: item.id },
          responseType: 'blob',
        });
        if (resp.error) {
          showError(
            await getUserFacingApiMessageAsync(
              resp,
              'Failed to download remittance file'
            )
          );
          return;
        }
        downloadResponseAsFile(
          resp,
          `remittance_${item.trace_number || item.id}.835`
        );
      } catch (err) {
        console.error('Failed to download remittance file', err);
        showError('Failed to download remittance file');
      } finally {
        setDownloadingId(null);
      }
    },
    [showError]
  );

  const handleExportRemittances = useCallback(
    async (format: ExportFormat) => {
      try {
        const listParams = query.toParams();
        // The export route rejects unknown query params, so `page`/`per_page`
        // are stripped — an export IS the full filtered table, not one page of
        // it — and the remaining filters are forwarded verbatim.
        const response = await exportRemittances({
          query: buildRemittanceExportParams(listParams, format),
          responseType: 'blob' as const,
        });
        if (response.error || !response.data) {
          throw new UserFacingExportError(
            await getUserFacingApiMessageAsync(
              {
                response: {
                  status: response.response?.status,
                  data: response.response?.data ?? response.error,
                },
              },
              'Unable to export remittances. Please try again.'
            )
          );
        }
        downloadResponseAsFile(response as never, `remittances.${format}`);
      } catch (error) {
        console.error('Failed to export remittances', error);
        if (error instanceof UserFacingExportError) throw error;
        throw new UserFacingExportError(
          await getUserFacingApiMessageAsync(
            error,
            'Unable to export remittances. Please try again.'
          )
        );
      }
    },
    [query]
  );

  const handleUploaded = useCallback(() => {
    query.setPage(0);
    // Reloads the list, the payor options (a new 835 can introduce an unseen
    // payor) and the status counts — the upload lands in Saved, so that pill
    // must grow.
    void refreshAll();
  }, [query, refreshAll]);

  const handleEditorSaved = useCallback(() => {
    query.setPage(0);
    void refreshAll();
    if (worksheetEditor?.mode === 'edit') {
      void queryClient.invalidateQueries({
        queryKey: billingKeys.remittanceDetail(worksheetEditor.remittanceId),
      });
    }
  }, [query, queryClient, refreshAll, worksheetEditor]);

  const handleLifecycle = useCallback(async () => {
    if (!pendingLifecycle) return;
    const { action, remittance } = pendingLifecycle;
    const copy = LIFECYCLE_COPY[action];
    setLifecycleSubmitting(true);
    try {
      const request = { path: { remittance_id: remittance.id } };
      const response =
        action === 'complete'
          ? await completeRemittance(request)
          : action === 'reopen'
            ? await reopenRemittance(request)
            : await deleteRemittance(request);
      if (response.error) {
        showError(getBusinessErrorMessage(response) ?? copy.error);
        return;
      }

      // A committed mutation must not leave Billing trapped behind a disabled
      // confirmation while unrelated invalidations settle. Restore focus now,
      // then verify it again after the next list result is applied.
      lifecycleTransitionTriggerRef.current = popupTriggerRef.current;
      lifecycleTransitionListAppliedRef.current = false;
      setPendingLifecycle(null);

      const lifecycleRefreshes = [
        refreshAll(),
        queryClient.invalidateQueries({
          queryKey: billingKeys.remittanceDetail(remittance.id),
        }),
      ];
      if (action === 'complete' || action === 'reopen') {
        lifecycleRefreshes.push(
          queryClient.invalidateQueries({
            queryKey: billingKeys.billingCycles(),
          })
        );
      }
      if (action === 'complete') {
        lifecycleRefreshes.push(
          queryClient.invalidateQueries({
            queryKey: billingKeys.billingMeta(),
          })
        );
      }
      try {
        await Promise.all(lifecycleRefreshes);
      } catch (refreshError) {
        // The lifecycle mutation already succeeded. Keep its dialog closed even
        // when a follow-up refetch fails.
        console.error(
          'Failed to refresh remittances after lifecycle action',
          refreshError
        );
      }
    } catch (error) {
      console.error(`Failed to ${action} remittance`, error);
      showError(copy.error);
    } finally {
      setLifecycleSubmitting(false);
    }
  }, [pendingLifecycle, queryClient, refreshAll, showError]);

  const columns = useMemo<ColumnDef<RemittanceSummary>[]>(
    () => [
      {
        id: 'check_date',
        accessorKey: 'check_date',
        header: 'Check Date',
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.check_date),
      },
      {
        id: 'deposit_date',
        accessorKey: 'deposit_date',
        header: 'Deposit Date',
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.deposit_date),
      },
      {
        id: 'trace_number',
        accessorKey: 'trace_number',
        header: 'Remittance #',
        enableSorting: true,
        cell: ({ row }) => row.original.trace_number || '—',
      },
      {
        id: 'payer_display',
        accessorKey: 'payer_display',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => row.original.payer_display || '—',
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ row }) => formatCurrency(row.original.amount),
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: 'Source',
        enableSorting: true,
        cell: ({ row }) => SOURCE_LABELS[row.original.source],
      },
      {
        id: 'claim_count',
        accessorKey: 'claim_count',
        header: 'Claims',
        enableSorting: true,
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-end gap-0.5'>
            {row.original.has_file && (
              <Button
                variant='ghost'
                size='sm'
                aria-label='Download 835 file'
                disabled={downloadingId === row.original.id}
                onClick={e => {
                  // Without this the download click also opens the detail dialog.
                  e.stopPropagation();
                  void handleDownloadRow(row.original);
                }}
              >
                {downloadingId === row.original.id ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Download className='h-4 w-4' />
                )}
              </Button>
            )}
            <DropdownMenu
              modal={false}
              onOpenChange={open => {
                if (!open) {
                  if (!menuPopupSelectionRef.current) {
                    popupTriggerRef.current = null;
                  }
                  menuPopupSelectionRef.current = false;
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  aria-label='Remittance actions'
                  disabled={lifecycleSubmitting}
                  onClick={event => {
                    event.stopPropagation();
                    popupTriggerRef.current = event.currentTarget;
                  }}
                  onKeyDown={event => {
                    popupTriggerRef.current = event.currentTarget;
                  }}
                  onPointerDown={event => {
                    event.stopPropagation();
                    popupTriggerRef.current = event.currentTarget;
                  }}
                >
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='end'
                onClick={event => event.stopPropagation()}
              >
                {row.original.status === 'saved' ? (
                  <>
                    <DropdownMenuItem
                      onClick={event => event.stopPropagation()}
                      onSelect={() => {
                        menuPopupSelectionRef.current = true;
                        setWorksheetEditor({
                          mode: 'edit',
                          remittanceId: row.original.id,
                        });
                      }}
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={event => event.stopPropagation()}
                      onSelect={() => {
                        menuPopupSelectionRef.current = true;
                        setPendingLifecycle({
                          action: 'complete',
                          remittance: row.original,
                        });
                      }}
                    >
                      Complete
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className='text-destructive focus:text-destructive'
                      onClick={event => event.stopPropagation()}
                      onSelect={() => {
                        menuPopupSelectionRef.current = true;
                        setPendingLifecycle({
                          action: 'delete',
                          remittance: row.original,
                        });
                      }}
                    >
                      Delete
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem
                    onClick={event => event.stopPropagation()}
                    onSelect={() => {
                      menuPopupSelectionRef.current = true;
                      setPendingLifecycle({
                        action: 'reopen',
                        remittance: row.original,
                      });
                    }}
                  >
                    Reopen
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [downloadingId, handleDownloadRow, lifecycleSubmitting]
  );

  return (
    <div className='flex flex-col gap-4'>
      {/* Status pipeline, styled to match the claims status tabs (same purple
          variant, same TabsList/trigger classes, same count pill). Two statuses
          only, so the grid caps at 2 columns instead of the claims bar's 7. */}
      <Tabs
        value={query.statusTab}
        onValueChange={value => query.setStatusTab(value as RemittanceStatus)}
        variant='purple'
      >
        <TabsList className='grid h-auto min-h-8 w-full grid-cols-1 gap-0.5 rounded-md border border-border bg-muted/40 p-1 min-[520px]:grid-cols-2'>
          {STATUS_TABS.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className='group min-h-8 whitespace-normal px-1 py-1.5 text-[11px] font-medium leading-tight [overflow-wrap:anywhere] data-[state=active]:shadow-none min-[520px]:px-2 sm:text-xs lg:text-[13px] xl:text-sm'
            >
              <span className='inline-flex min-w-0 max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center [overflow-wrap:anywhere] sm:flex-nowrap'>
                <span className='min-w-0 [overflow-wrap:anywhere] sm:whitespace-nowrap'>
                  {tab.label}
                </span>
                <TabCountPill
                  label={tab.label}
                  value={statusCounts ? String(statusCounts[tab.id] ?? 0) : '-'}
                />
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <ServerDataTable
        data={items}
        columns={columns}
        getRowId={row => row.id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        emptyMessage='No remittances match your filters'
        onRowClick={row => setSelectedRemittanceId(row.id)}
        toolbar={
          <RemittanceTableToolbar
            query={query}
            payors={payors}
            onReset={query.reset}
            uploadSlot={
              <>
                {canExportRemittances ? (
                  <ExportMenuButton onExport={handleExportRemittances} />
                ) : null}
                <Button
                  ref={newRemittanceTriggerRef}
                  size='default'
                  onClick={event => {
                    popupTriggerRef.current = event.currentTarget;
                    setWorksheetEditor({ mode: 'create' });
                  }}
                >
                  <Plus className='mr-1 h-4 w-4' />
                  New Remittance
                </Button>
                <UploadButton onUploaded={handleUploaded} />
              </>
            }
          />
        }
      />
      <RemittanceDetailDialog
        remittanceId={selectedRemittanceId}
        onClose={() => setSelectedRemittanceId(null)}
      />
      {worksheetEditor && (
        <RemittanceWorksheetEditor
          mode={worksheetEditor.mode}
          remittanceId={
            worksheetEditor.mode === 'edit'
              ? worksheetEditor.remittanceId
              : undefined
          }
          onClose={() => setWorksheetEditor(null)}
          onSaved={handleEditorSaved}
        />
      )}
      <ConfirmationDialog
        open={!!pendingLifecycle}
        title={
          pendingLifecycle ? LIFECYCLE_COPY[pendingLifecycle.action].title : ''
        }
        message={
          pendingLifecycle
            ? LIFECYCLE_COPY[pendingLifecycle.action].message
            : ''
        }
        confirmLabel={
          pendingLifecycle
            ? LIFECYCLE_COPY[pendingLifecycle.action].confirmLabel
            : ''
        }
        loading={lifecycleSubmitting}
        onAccept={() => void handleLifecycle()}
        onCancel={() => setPendingLifecycle(null)}
      />
    </div>
  );
});

RemittanceManager.displayName = 'RemittanceManager';

export default RemittanceManager;
